import { t as tr } from '../i18n.js'
import { useQuery } from '@tanstack/react-query'
import { Check, Diamond } from 'lucide-react'
import { api, type ProjectDashboard as PD } from '../api.js'
import { fmtMinutes } from '../time.js'
import { Stat, WeeklyBars } from './charts.js'
import { Progress } from './Home.js'

export function ProjectDashboardPanel({ projectId }: { projectId: string }) {
  const q = useQuery({ queryKey: ['project-dashboard', projectId], queryFn: () => api<PD>(`/projects/${projectId}/dashboard`) })
  if (!q.data) return null
  const d = q.data
  const est = d.hours.estimated, logged = d.hours.logged / 60
  return (
    <>
      <div className="stats">
        <Stat n={`${d.tasks.total ? Math.round((d.tasks.done / d.tasks.total) * 100) : 0}%`} label={tr("tasks done")} sub={`${d.tasks.done} of ${d.tasks.total} · ${d.tasks.inProgress} in progress`} />
        <Stat n={d.tasks.overdue} label={tr("overdue")} sub={`${d.tasks.dueSoon} due in 7 days · ${d.tasks.unassigned} unassigned`} bad={d.tasks.overdue > 0} />
        <Stat n={fmtMinutes(d.hours.logged)} label={d.canSeeAllTime ? tr("hours logged") : tr("your hours logged")} sub={est ? `of ${est}h estimated (${Math.round((logged / est) * 100)}%)` : 'no estimates yet'} bad={est > 0 && logged > est} />
        <Stat n={fmtMinutes(d.hours.logged30d)} label={tr("last 30 days")} sub={d.hours.billableShare !== null ? `${Math.round(d.hours.billableShare * 100)}% billable` : undefined} />
      </div>
      <div className="grid2">
        <section className="card stack">
          <h2>Hours per week{d.canSeeAllTime ? '' : ' (yours)'}</h2>
          {d.insufficientHistory
            ? <p className="muted small">{tr("Not enough history yet — the chart appears once two project weeks have entries.")}</p>
            : <WeeklyBars points={d.weekly} weeks={12} title="Measured in the project's timezone-of-truth" />}
          <Progress done={d.tasks.done} total={d.tasks.total} />
        </section>
        <section className="card stack">
          <h2>{tr("Team (last 30 days)")}</h2>
          <table className="table">
            <thead><tr><th>{tr("Member")}</th><th className="num">{tr("Hours")}</th><th className="num">{tr("Open tasks")}</th></tr></thead>
            <tbody>
              {d.members.map((m) => <tr key={m.userId}><td>{m.displayName} <span className="muted small">{m.role === 'admin' ? 'admin' : m.role}</span></td><td className="num mono">{m.minutes30d ? fmtMinutes(m.minutes30d) : <span className="muted">—</span>}</td><td className="num mono">{m.openTasks || ''}</td></tr>)}
            </tbody>
          </table>
          {!d.canSeeAllTime && <p className="muted small">{tr("Hours for other members are visible to project administrators and managers.")}</p>}
        </section>
      </div>
      {d.milestones.length > 0 && (
        <section className="card stack">
          <h2>{tr("Milestones")}</h2>
          {d.milestones.map((m) => (
            <div key={m.id} className="row between">
              <span className={m.completedAt ? 'muted' : ''}>{m.completedAt ? <Check size={12} aria-hidden /> : <Diamond size={12} aria-hidden />} {m.name} <span className={`small ${m.overdue ? 'bad' : 'muted'}`}>{m.dueOn ?? ''}{m.overdue ? ' · overdue' : ''}</span></span>
              <span style={{ minWidth: '10rem' }}><Progress done={m.tasksDone} total={m.tasksTotal} /></span>
            </div>
          ))}
        </section>
      )}
    </>
  )
}
